import {
  decodeNumericCharacterReferences,
  decodeStyleCharacterReferences,
  findForbiddenCharacterReference,
  isSafeCssValue,
  scanCssDeclarations,
  HTML_WHITESPACE_PATTERN,
  isHtmlWhitespace,
  normalizeHref,
  trimHtmlWhitespace,
} from './characters';
import {
  allowedAttributesByTag,
  allowedHrefPattern,
  allowedTags,
  allowedTextAlignments,
  backgroundColorTags,
  externalHrefPattern,
  externalLinkAttributes,
  globalAllowedAttributes,
  preservedStyleProperties,
  preservedStylePropertyPrefixes,
  richTextHtmlLimits,
  safeColorPattern,
  textColorTags,
} from './policy';
import { IRichTextHtmlViolation } from './types';

/**
 * Strict validation of a submitted Rich text value, without a DOM and without a dependency.
 *
 * The contract is enforced by refusing anything the policy does not describe, rather than by
 * cleaning it. That is deliberately easier to get right than sanitization: a validator only has to
 * recognize the small grammar the configured editor emits, and everything else — unknown tags,
 * unknown attributes, comments, processing instructions, unquoted values, stray `<` or `&` — is a
 * violation. The authoring client is what produces conformant HTML; the service just refuses to
 * store anything else.
 *
 * Text content itself carries no rule: a bare `&` cannot introduce an element or escape an
 * attribute value, since HTML decodes entities after delimiting, and escaped text such as
 * `&lt;script&gt;` is exactly what a legal document is allowed to contain.
 *
 * A value containing no `<` at all is plain text and always valid, and a `<` that does not open a
 * tag is treated as text for the same reason. That keeps clients that predate the rich-text
 * contract working, and it is safe because every consumer either escapes or element-maps text
 * rather than injecting it.
 *
 * THE INVARIANT THIS FILE HAS TO HOLD. Everything below is a hand-written scanner standing in for
 * an HTML tokenizer, so every rule has to agree with what a parser does with the same bytes, not
 * with what the rule reads like. Four consequences, none of them optional:
 *
 *  1. Only the five characters in `HTML_WHITESPACE` separate anything. JavaScript's `\s` matches
 *     twenty more that a tokenizer treats as ordinary characters, and markup fits in that gap.
 *  2. A quote opens an attribute value only in the before-attribute-value state — after `=`.
 *     Anywhere else it belongs to an attribute name and the tag still ends at the next `>`.
 *  3. A trailing solidus closes only a void element. On anything else a parser reports a parse
 *     error and keeps the element open.
 *  4. Character references in an attribute value are decoded AFTER the attribute is delimited, so
 *     a grammar applied to the raw value is not the grammar the parser will apply.
 *
 * A regression here is not a cosmetic disagreement: the gateway stores what this file accepts,
 * byte for byte.
 */

/** Elements that carry no closing tag. */
const VOID_TAGS = ['br', 'col', 'hr'] as const;

/** `data-type` values the configured editor emits. */
const ALLOWED_DATA_TYPES = ['horizontalRule', 'taskList', 'taskItem'] as const;

const TAG_NAME_PATTERN = /^[a-z][a-z0-9]*$/;
/** What a browser treats as the start of a tag: `<name` or `</name`. */
const TAG_START_PATTERN = /^\/?[a-zA-Z]/;
const ATTRIBUTE_PATTERN = /([a-zA-Z-]+)[\t\n\f\r ]*=[\t\n\f\r ]*("([^"]*)"|'([^']*)')|([a-zA-Z-]+)/g;

/** The policy surface the validator reads, so a host can validate against one of its own. */
export interface IRichTextHtmlValidatorPolicy {
  allowedAttributesByTag: Readonly<Record<string, readonly string[]>>;
  allowedHrefPattern: RegExp;
  allowedTags: readonly string[];
  allowedTextAlignments: readonly string[];
  backgroundColorTags: readonly string[];
  externalHrefPattern: RegExp;
  externalLinkAttributes: Readonly<Record<string, string>>;
  globalAllowedAttributes: readonly string[];
  maxNestingDepth: number;
  preservedStyleProperties: readonly string[];
  preservedStylePropertyPrefixes: readonly string[];
  safeColorPattern: RegExp;
  textColorTags: readonly string[];
}

/** The shipped policy, bound once so callers that override nothing share a single object. */
export const defaultRichTextHtmlValidatorPolicy: IRichTextHtmlValidatorPolicy = {
  allowedAttributesByTag,
  allowedHrefPattern,
  allowedTags,
  allowedTextAlignments,
  backgroundColorTags,
  externalHrefPattern,
  externalLinkAttributes,
  globalAllowedAttributes,
  maxNestingDepth: richTextHtmlLimits.maxNestingDepth,
  preservedStyleProperties,
  preservedStylePropertyPrefixes,
  safeColorPattern,
  textColorTags,
};

/**
 * One pass over one value.
 *
 * A fresh instance per call, on purpose: the violation list, the open-element stack and the
 * depth flag are the state of a single parse. Holding them on a long-lived validator would let two
 * overlapping calls share them — harmless while the scan is fully synchronous, and a silent
 * cross-request leak the moment it stops being.
 */
class RichTextHtmlScan {
  private readonly violations: IRichTextHtmlViolation[] = [];

  private readonly stack: string[] = [];

  private depthReported = false;

  constructor(
    private readonly policy: IRichTextHtmlValidatorPolicy,
    private readonly value: string,
  ) {}

  run(): IRichTextHtmlViolation[] {
    let index = 0;

    while (index < this.value.length) {
      const next = this.value.indexOf('<', index);

      if (next === -1) break;

      if (this.value.startsWith('<!', next) || this.value.startsWith('<?', next)) {
        this.add('markup-declaration', 'comments, doctypes and processing instructions are not allowed');

        return this.violations;
      }

      // `</` must name an element. A browser reads `</>` as a bogus comment and drops it, which
      // would remove text from a legal document without telling anyone.
      if (this.value.startsWith('</', next) && !/^[a-zA-Z]/.test(this.value.slice(next + 2, next + 3))) {
        this.add('malformed-tag', 'the value contains a closing bracket that names no element');

        return this.violations;
      }

      // A `<` that does not open a tag is literal text, exactly as a browser reads it: `a < b` in a
      // legal document is a comparison, not broken markup. It cannot start an element, so accepting
      // it costs nothing and keeps clients that submit plain text working.
      if (!TAG_START_PATTERN.test(this.value.slice(next + 1, next + 3))) {
        index = next + 1;
        continue;
      }

      const close = RichTextHtmlScan.findTagEnd(this.value, next + 1);

      if (close === -1) {
        this.add('malformed-tag', 'the value ends inside a tag');

        return this.violations;
      }

      this.tag(this.value.slice(next + 1, close));
      index = close + 1;
    }

    this.stack.reverse().forEach(tag => this.add('unclosed', `<${tag}> is never closed`));

    return this.violations;
  }

  /**
   * Find the `>` that closes a tag, following the tokenizer's own rule for when a quote is special.
   *
   * A quote opens a quoted attribute value ONLY from the before-attribute-value state, i.e. directly
   * after `=`, whitespace between them allowed. A quote anywhere else — `<p title=x"y>` — is a parse
   * error the tokenizer folds into the attribute NAME while it keeps scanning, so the `>` after it
   * still ends the tag. A scanner that toggled on every quote runs past that `>` and hands the
   * caller a "tag" with live markup inside it, which for an end tag is never attribute-scanned.
   */
  private static findTagEnd(value: string, from: number): number {
    let quote = '';
    let afterEquals = false;

    for (let index = from; index < value.length; index += 1) {
      const character = value[index];

      if (quote) {
        if (character === quote) quote = '';

        continue;
      }

      if (character === '>') return index;

      if (character === '=') {
        afterEquals = true;

        continue;
      }

      // Whitespace does not leave the before-attribute-value state: `title = "a > b"` is one value.
      if (isHtmlWhitespace(character)) continue;

      if (afterEquals && (character === '"' || character === "'")) {
        quote = character;
        afterEquals = false;

        continue;
      }

      afterEquals = false;
    }

    return -1;
  }

  private add(code: string, detail: string): void {
    this.violations.push({ code, detail });
  }

  private tag(source: string): void {
    const hasSolidus = source.endsWith('/');
    const body = hasSolidus ? source.slice(0, -1) : source;
    const closing = body.startsWith('/');
    const content = trimHtmlWhitespace(closing ? body.slice(1) : body);
    const separator = content.search(HTML_WHITESPACE_PATTERN);
    const tag = (separator === -1 ? content : content.slice(0, separator)).toLowerCase();
    const remainder = separator === -1 ? '' : content.slice(separator);

    if (!TAG_NAME_PATTERN.test(tag)) {
      this.add('malformed-tag', `"${tag}" is not a valid tag name`);

      return;
    }

    if (!this.policy.allowedTags.includes(tag)) {
      this.add('tag', `<${tag}> is not allowed by the policy`);

      return;
    }

    const isVoid = (VOID_TAGS as readonly string[]).includes(tag);

    if (closing) {
      // A parser discards everything after the name in an end tag, so anything here is markup the
      // author wrote and the rendered document will not contain — including a `"` this scanner
      // would otherwise have to reason about.
      if (trimHtmlWhitespace(remainder) || hasSolidus) {
        this.add('closing-tag-content', `</${tag}> must not carry anything after the tag name`);
      }

      if (isVoid) this.add('void-close', `<${tag}> must not have a closing tag`);
      else if (this.stack.pop() !== tag) this.add('unbalanced', `</${tag}> does not close the current element`);

      return;
    }

    // HTML honours a trailing solidus only on a void element. Everywhere else it is a parse error a
    // parser IGNORES — the element stays open and swallows the rest of the document — so treating
    // it as "already closed" would disable the only structural check there is.
    if (hasSolidus && !isVoid) {
      this.add('self-closing', `<${tag}/> is not self-closing in HTML; write <${tag}></${tag}> instead`);
    }

    const attributes = this.attributes(tag, remainder, separator !== -1);

    if (tag === 'a') this.link(attributes);

    if (isVoid) return;

    this.stack.push(tag);

    // Without a ceiling a short document expands into an arbitrarily deep tree that every
    // downstream parser has to build, and that no editor can clear once it is stored.
    if (this.stack.length > this.policy.maxNestingDepth && !this.depthReported) {
      this.depthReported = true;
      this.add('nesting-depth', `the value nests elements more than ${this.policy.maxNestingDepth} deep`);
    }
  }

  /**
   * Scan the attribute section of a start tag.
   *
   * The gap check is what makes a regex scan defensible here: any unconsumed non-whitespace text
   * between matches means the scanner did not understand the tag, and the value is refused rather
   * than accepted on a partial reading.
   */
  private attributes(tag: string, source: string, present: boolean): Map<string, string | null> {
    const attributes = new Map<string, string | null>();

    if (!present) return attributes;

    let consumedUntil = 0;

    ATTRIBUTE_PATTERN.lastIndex = 0;

    let match = ATTRIBUTE_PATTERN.exec(source);

    while (match) {
      const [, quotedName, , doubleQuoted, singleQuoted, bareName] = match;
      const name = (bareName ?? quotedName).toLowerCase();

      if (trimHtmlWhitespace(source.slice(consumedUntil, match.index))) {
        this.add('malformed-attribute', `<${tag}> contains malformed attribute syntax`);
      }

      if (attributes.has(name)) this.add('duplicate-attribute', `<${tag}> repeats the attribute "${name}"`);
      else attributes.set(name, bareName ? null : (doubleQuoted ?? singleQuoted ?? ''));

      consumedUntil = ATTRIBUTE_PATTERN.lastIndex;
      match = ATTRIBUTE_PATTERN.exec(source);
    }

    if (trimHtmlWhitespace(source.slice(consumedUntil))) {
      this.add('malformed-attribute', `<${tag}> contains malformed attribute syntax`);
    }

    attributes.forEach((value, name) => this.attribute(tag, name, value));

    return attributes;
  }

  private attribute(tag: string, name: string, rawValue: string | null): void {
    const attribute = name.toLowerCase();
    const allowed = new Set<string>([
      ...this.policy.globalAllowedAttributes,
      ...(this.policy.allowedAttributesByTag[tag] ?? []),
    ]);

    if (!allowed.has(attribute)) {
      this.add('attribute', `<${tag}> may not carry the attribute "${attribute}"`);

      return;
    }

    if (rawValue === null) {
      this.add('unquoted-attribute', `attribute "${attribute}" on <${tag}> must have a quoted value`);

      return;
    }

    if (attribute === 'style') this.style(tag, rawValue);

    if (attribute === 'href') {
      // `&amp;` is inert here: it decodes to `&`, which cannot introduce a scheme or an authority,
      // and a legitimate query string round-trips to it. Anything else is refused.
      const named = findForbiddenCharacterReference(rawValue, ['amp']);

      if (named) this.add('href', `<a> has a character reference "${named}" in its href`);
      else if (!this.policy.allowedHrefPattern.test(normalizeHref(decodeNumericCharacterReferences(rawValue)))) {
        this.add('href', `<a> has an href the policy does not allow`);
      }
    }

    // The remaining grammars are exact matches and are applied to the RAW value on purpose:
    // decoding first would admit `target="&#95;blank"`, and refusing it costs nothing.
    if (attribute === 'target' && rawValue !== this.policy.externalLinkAttributes.target) {
      this.add('link-target', `<a> target must be "${this.policy.externalLinkAttributes.target}"`);
    }

    if (attribute === 'rel' && rawValue !== this.policy.externalLinkAttributes.rel) {
      this.add('link-rel', `<a> rel must be "${this.policy.externalLinkAttributes.rel}"`);
    }

    if (attribute === 'data-checked' && rawValue !== 'true' && rawValue !== 'false') {
      this.add('data-checked', `data-checked must be "true" or "false"`);
    }

    if (attribute === 'data-type' && !(ALLOWED_DATA_TYPES as readonly string[]).includes(rawValue)) {
      this.add('data-type', `data-type "${rawValue}" is not one the editor emits`);
    }
  }

  private style(tag: string, rawStyle: string): void {
    // Three steps, and the order carries the weight: decode, refuse what is still a reference,
    // then split.
    //
    // Decoding before REFUSING leaves the decoder as the only place that says which references are
    // tolerated, so whatever survives is by definition one a parser will not decode — hence the
    // empty inert list below, which refuses all of them.
    //
    // Decoding before SPLITTING is a separate reason: `&quot;` ends in a semicolon, so splitting
    // first would cut it in half, and `margin:0&#59display:none` is two declarations once decoded.
    const decoded = decodeStyleCharacterReferences(rawStyle);
    const named = findForbiddenCharacterReference(decoded, []);

    if (named) {
      this.add('style-entity', `<${tag}> has a character reference "${named}" in its style attribute`);

      return;
    }

    const { declarations, unterminated } = scanCssDeclarations(decoded);

    // An unclosed string swallows every declaration after it, so stored and rendered disagree.
    if (unterminated) {
      this.add('style-value', `<${tag}> has an unterminated string in its style attribute`);

      return;
    }

    declarations
      .map(declaration => trimHtmlWhitespace(declaration.trim()))
      .filter(Boolean)
      .forEach(declaration => this.declaration(tag, declaration));
  }

  private declaration(tag: string, declaration: string): void {
    const separator = declaration.indexOf(':');

    if (separator === -1) {
      this.add('malformed-style', `<${tag}> has a malformed style declaration "${declaration}"`);

      return;
    }

    const property = declaration.slice(0, separator).trim().toLowerCase();
    const declared = declaration
      .slice(separator + 1)
      .trim()
      .toLowerCase();

    if (property === 'text-align') {
      if (!this.policy.allowedTextAlignments.includes(declared)) {
        this.add('style-value', `<${tag}> has text-align "${declared}", which the policy does not allow`);
      }

      return;
    }

    if (property === 'background-color') {
      if (!this.policy.backgroundColorTags.includes(tag)) {
        this.add('style-property', `<${tag}> may not carry background-color`);
      } else if (!this.policy.safeColorPattern.test(declared)) {
        this.add('style-value', `<${tag}> has background-color "${declared}", which is not a supported colour`);
      }

      return;
    }

    if (property === 'color') {
      if (!this.policy.textColorTags.includes(tag)) {
        this.add('style-property', `<${tag}> may not carry color`);
      } else if (!this.policy.safeColorPattern.test(declared)) {
        this.add('style-value', `<${tag}> has color "${declared}", which is not a supported colour`);
      }

      return;
    }

    const isPreserved =
      this.policy.preservedStyleProperties.includes(property) ||
      this.policy.preservedStylePropertyPrefixes.some(
        prefix => property === prefix || property.startsWith(`${prefix}-`),
      );

    if (!isPreserved) {
      this.add('style-property', `<${tag}> may not carry the style property "${property}"`);

      return;
    }

    if (!declared) this.add('style-value', `<${tag}> has an empty ${property}`);
    // A comment opened in a preserved value swallows whatever the sanitizer appends after it, so
    // the stored declarations and the rendered ones stop agreeing.
    else if (declared.includes('/*') || declared.includes('*/')) {
      this.add('style-value', `<${tag}> has a comment inside its ${property} value`);
    }
    // Everything else is the grammar's call rather than another list of shapes to refuse: a value
    // outside it is one this module cannot claim to read the way a browser will.
    else if (!isSafeCssValue(declared)) {
      this.add('style-value', `<${tag}> has a ${property} value outside the supported grammar`);
    }
  }

  /**
   * The link rules bind three attributes together, so they cannot be checked one at a time.
   *
   * An absolute `http:` or `https:` target must carry opener isolation, and nothing else may carry
   * it: a fragment or root-relative target with `target="_blank"` would leave the inspection page
   * for no reason. A link with no target at all is what the authoring client produces for an unsafe
   * one — the text stays, the navigation goes — so it is valid.
   */
  private link(attributes: Map<string, string | null>): void {
    const href = normalizeHref(decodeNumericCharacterReferences(attributes.get('href') ?? ''));
    const hasTarget = attributes.has('target');
    const hasRel = attributes.has('rel');

    if (!attributes.has('href')) {
      if (hasTarget || hasRel) this.add('link-attributes', '<a> without an href must not carry target or rel');

      return;
    }

    if (this.policy.externalHrefPattern.test(href)) {
      if (!hasTarget || !hasRel) {
        this.add(
          'link-isolation',
          `<a> to an absolute http(s) target must carry target="${this.policy.externalLinkAttributes.target}" and rel="${this.policy.externalLinkAttributes.rel}"`,
        );
      }

      return;
    }

    if (hasTarget || hasRel) {
      this.add(
        'link-attributes',
        '<a> to a fragment, root-relative, mail or phone target must not carry target or rel',
      );
    }
  }
}

/**
 * A validator bound to one policy. Reusable and stateless: every call builds its own scan.
 *
 * Mirrors `RichTextHtmlPipeline`: what the module cannot decide for itself arrives through the
 * constructor instead of being reached for at module scope.
 */
export class RichTextHtmlValidator {
  private readonly policy: IRichTextHtmlValidatorPolicy;

  constructor(policy: IRichTextHtmlValidatorPolicy = defaultRichTextHtmlValidatorPolicy) {
    this.policy = policy;
  }

  /** The reasons this value cannot be stored. An empty array means it may be. */
  validate(value?: string | null): IRichTextHtmlViolation[] {
    if (!value) return [];
    if (!value.includes('<')) return [];

    return new RichTextHtmlScan(this.policy, value).run();
  }

  /** The shipped policy, for callers that do not need one of their own. */
  static readonly default = new RichTextHtmlValidator();
}
